import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Button, ButtonProps, Dialog, IconButton, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tone } from '../../../utils/uiTokens'
import { LEVEL_COLORS } from '../../curator/ratingPage/ratingLevel'
import { Preset, hm, parse, pad, presetRange, todayKey, fmtDate } from './format'

export const TONES = {
    ok: tone.ok,
    wait: tone.warn,
    bad: tone.bad,
    grey: tone.neutral,
    pink: { bg: ui.pinkSoft, text: ui.pink, border: ui.pinkSoft },
    blue: tone.info,
}
export type Tone = keyof typeof TONES

export const pageSx = { margin: '10px', '@media (max-width: 500px)': { margin: '8px 0' } }
export const contentSx = {
    background: ui.card,
    borderRadius: '16px',
    padding: '20px',
    minHeight: '80vh',
    '@media (max-width: 500px)': { padding: '14px 10px', borderRadius: 0 },
}

export const PageHead: React.FC<{ title: string; sub?: React.ReactNode; action?: React.ReactNode }> = ({ title, sub, action }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', marginBottom: '16px' }}>
        <Box sx={{ minWidth: 0 }}>
            <H3>{title}</H3>
            {sub && <Box sx={{ color: ui.sub, fontSize: '13.5px', marginTop: '6px', maxWidth: 720 }}>{sub}</Box>}
        </Box>
        {action}
    </Box>
)

export const Card: React.FC<{ children: React.ReactNode; sx?: any; pad?: boolean }> = ({ children, sx, pad = true }) => (
    <Box
        sx={{
            border: `1px solid ${ui.border}`,
            borderRadius: '14px',
            background: ui.card,
            padding: pad ? '16px 18px' : 0,
            marginBottom: '16px',
            minWidth: 0,
            '@media (max-width: 500px)': { padding: pad ? '12px' : 0 },
            ...sx,
        }}
    >
        {children}
    </Box>
)

export const CardHead: React.FC<{ title: React.ReactNode; right?: React.ReactNode }> = ({ title, right }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', marginBottom: '12px' }}>
        <Box sx={{ fontWeight: 700, fontSize: '15px', color: ui.ink }}>{title}</Box>
        {right && <Box sx={{ fontSize: '13px', color: ui.muted }}>{right}</Box>}
    </Box>
)

export const Pill: React.FC<{ tone?: Tone; children: React.ReactNode; sx?: any }> = ({ tone = 'grey', children, sx }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            borderRadius: '100px',
            fontSize: '12px',
            fontWeight: 700,
            lineHeight: 1.4,
            background: TONES[tone].bg,
            color: TONES[tone].text,
            border: `1px solid ${TONES[tone].border}`,
            whiteSpace: 'nowrap',
            ...sx,
        }}
    >
        {children}
    </Box>
)

export const Muted: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
    <Box sx={{ fontSize: '12px', color: ui.muted, ...sx }}>{children}</Box>
)

export const Hint: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
    <Box sx={{ fontSize: '12.5px', color: ui.muted, lineHeight: 1.45, ...sx }}>{children}</Box>
)

export const Empty: React.FC<{ text?: string }> = ({ text }) => {
    const { formatMessage } = useIntl()
    return <Box sx={{ color: ui.muted, fontSize: '13px', padding: '24px 8px', textAlign: 'center' }}>{text || formatMessage({ id: 'noData' })}</Box>
}

export const Chip: React.FC<{ on: boolean; onClick: () => void; tone?: 'pink' | 'bad'; children: React.ReactNode; disabled?: boolean }> = ({
    on,
    onClick,
    tone = 'pink',
    children,
    disabled,
}) => {
    const c = tone === 'bad' ? TONES.bad : TONES.pink
    return (
        <Box
            component="button"
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={onClick}
            sx={{
                border: `1px solid ${on ? c.text : ui.border}`,
                background: on ? c.bg : ui.card,
                color: on ? c.text : ui.ink,
                fontWeight: on ? 700 : 500,
                fontSize: '13px',
                borderRadius: '100px',
                padding: '6px 12px',
                cursor: disabled ? 'default' : 'pointer',
                font: 'inherit',
                lineHeight: 1.3,
                '&:hover': { borderColor: disabled ? ui.border : c.text },
            }}
        >
            {children}
        </Box>
    )
}

export const Chips: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{children}</Box>
)

export const Kpis: React.FC<{ items: { label: string; value: React.ReactNode; sub?: React.ReactNode; bad?: boolean }[] }> = ({ items }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
        {items.map((k) => (
            <Box key={k.label} sx={{ border: `1px solid ${k.bad ? TONES.bad.border : ui.border}`, borderRadius: '12px', padding: '12px 14px', background: k.bad ? TONES.bad.bg : ui.surface }}>
                <Box sx={{ fontSize: '12px', color: k.bad ? TONES.bad.text : ui.sub }}>{k.label}</Box>
                <Box sx={{ fontSize: '20px', fontWeight: 800, color: k.bad ? TONES.bad.text : ui.ink, marginTop: '4px', overflowWrap: 'anywhere' }}>{k.value}</Box>
                {k.sub && <Muted sx={{ marginTop: '2px' }}>{k.sub}</Muted>}
            </Box>
        ))}
    </Box>
)

// Таблица целиком прокручивается вбок на телефоне, страница - нет
export const TableWrap: React.FC<{ children: React.ReactNode; minWidth?: number }> = ({ children, minWidth = 720 }) => (
    <Box
        sx={{
            overflowX: 'auto',
            width: '100%',
            '& table': { width: '100%', minWidth, borderCollapse: 'collapse' },
            '& th': {
                textAlign: 'left',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                color: ui.sub,
                background: ui.surface,
                padding: '10px 12px',
                borderBottom: `1px solid ${ui.border}`,
                whiteSpace: 'nowrap',
            },
            '& td': { padding: '10px 12px', borderBottom: `1px solid ${ui.border}`, fontSize: '13.5px', color: ui.ink, verticalAlign: 'top' },
            '& .num': { textAlign: 'right', whiteSpace: 'nowrap' },
            '& .c': { textAlign: 'center' },
            '& .r': { textAlign: 'right' },
            '& tr.ret td': { background: tone.bad.bg },
            '& tr.det td': { background: ui.surface },
        }}
    >
        {children}
    </Box>
)

export const DetailsButton: React.FC<{ open: boolean; onClick: () => void; closedText?: string }> = ({ open, onClick, closedText }) => {
    const { formatMessage } = useIntl()
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{ border: 'none', background: 'transparent', color: ui.pink, fontWeight: 600, fontSize: '12.5px', cursor: 'pointer', padding: '4px 0', font: 'inherit' }}
        >
            {open ? formatMessage({ id: 'crmHide' }) : closedText || formatMessage({ id: 'crmDetails' })}
        </Box>
    )
}

// Пары «подпись - значение» в карточке
export const InfoBox: React.FC<{ title: string; rows: [string, React.ReactNode][] }> = ({ title, rows }) => (
    <Box sx={{ border: `1px solid ${ui.border}`, borderRadius: '12px', padding: '12px 14px', minWidth: 0 }}>
        <Box sx={{ fontSize: '12px', fontWeight: 700, color: ui.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>{title}</Box>
        <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, auto) 1fr', gap: '6px 12px', margin: 0, fontSize: '13px' }}>
            {rows.map(([k, v], i) => (
                <React.Fragment key={`${k}-${i}`}>
                    <Box component="dt" sx={{ color: ui.sub }}>{k}</Box>
                    <Box component="dd" sx={{ margin: 0, color: ui.ink, overflowWrap: 'anywhere' }}>{v ?? '-'}</Box>
                </React.Fragment>
            ))}
        </Box>
    </Box>
)

export const InfoGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px' }}>{children}</Box>
)

// Просмотр фото: все фото шага друг под другом
export const Lightbox: React.FC<{ urls: string[] | null; onClose: () => void }> = ({ urls, onClose }) => (
    <Dialog open={!!urls?.length} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { background: ui.ink, margin: '12px', width: 'calc(100% - 24px)' } }}>
        <Box sx={{ position: 'relative', padding: '40px 12px 12px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <IconButton onClick={onClose} sx={{ position: 'absolute', top: 4, right: 4, color: '#fff' }} aria-label="close">
                <CloseIcon />
            </IconButton>
            {(urls ?? []).map((u) => (
                <img key={u} src={u} alt="" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }} />
            ))}
        </Box>
    </Dialog>
)

export const useNow = (ms = 20000) => {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), ms)
        return () => clearInterval(t)
    }, [ms])
    return now
}

// Таймер этапа: дедлайн считает course-service, здесь только остаток.
// Зелёный > 10 мин, жёлтый <= 10, красный - просрочено, «ертең HH:mm дейін» если дедлайн не сегодня.
export const CrmTimer: React.FC<{ deadlineAt?: string | null; closedAt?: string | null; big?: boolean }> = ({ deadlineAt, closedAt, big }) => {
    const { formatMessage } = useIntl()
    const now = useNow(20000)
    const dl = parse(deadlineAt)
    if (!dl) return null
    const fin = parse(closedAt)
    let tone: Tone
    let text: string
    if (fin) {
        const ok = fin.getTime() <= dl.getTime()
        tone = ok ? 'ok' : 'bad'
        text = formatMessage({ id: ok ? 'crmTimerOnTime' : 'crmTimerLateDone' }, { time: hm(closedAt) })
    } else {
        const diff = Math.round((dl.getTime() - now) / 60000)
        if (diff < 0) {
            tone = 'bad'
            const late = -diff
            text =
                late >= 60
                    ? formatMessage({ id: 'crmTimerLateH' }, { h: Math.floor(late / 60), min: late % 60 })
                    : formatMessage({ id: 'crmTimerLate' }, { min: late })
        } else if (todayKey() !== `${dl.getFullYear()}-${pad(dl.getMonth() + 1)}-${pad(dl.getDate())}`) {
            tone = 'grey'
            const tomorrow = new Date(now)
            tomorrow.setDate(tomorrow.getDate() + 1)
            text =
                tomorrow.toDateString() === dl.toDateString()
                    ? formatMessage({ id: 'crmTimerTomorrow' }, { time: hm(deadlineAt) })
                    : formatMessage({ id: 'crmTimerUntil' }, { date: fmtDate(deadlineAt).slice(0, 5), time: hm(deadlineAt) })
        } else {
            tone = diff <= 10 ? 'wait' : 'ok'
            text = formatMessage({ id: 'crmTimerLeft' }, { min: diff })
        }
    }
    return (
        <Pill tone={tone} sx={big ? { fontSize: '14px', padding: '6px 14px' } : undefined}>
            {text}
        </Pill>
    )
}

export const usePeriod = (initial: Preset = 'month') => {
    const [preset, setPreset] = useState<Preset>(initial)
    const [[from, to], setRange] = useState<[string, string]>(() => presetRange(initial))
    return {
        preset,
        from,
        to,
        choose: (p: Preset) => {
            setPreset(p)
            setRange(presetRange(p))
        },
        setFrom: (v: string) => {
            if (!v) return
            setPreset('')
            setRange([v, v > to ? v : to])
        },
        setTo: (v: string) => {
            if (!v) return
            setPreset('')
            setRange([v < from ? v : from, v])
        },
    }
}

export type Period = ReturnType<typeof usePeriod>

const PRESETS: Preset[] = ['today', 'yesterday', 'week', 'month', 'all']

export const PeriodBar: React.FC<{ period: Period; children?: React.ReactNode }> = ({ period, children }) => {
    const { formatMessage } = useIntl()
    return (
        <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Chips>
                {PRESETS.map((p) => (
                    <Chip key={p} on={period.preset === p} onClick={() => period.choose(p)}>
                        {formatMessage({ id: `crmPreset_${p}` })}
                    </Chip>
                ))}
            </Chips>
            <DateField label={formatMessage({ id: 'crmFrom' })} value={period.from} onChange={period.setFrom} />
            <DateField label={formatMessage({ id: 'crmTo' })} value={period.to} onChange={period.setTo} />
            {children}
        </Box>
    )
}

export const DateField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
    <Box sx={{ width: 160 }}>
        <FieldLabel>{label}</FieldLabel>
        <TextField type="date" size="small" fullWidth value={value} onChange={(e) => onChange(e.target.value)} />
    </Box>
)

export const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean; hint?: string }> = ({ children, required, hint }) => (
    <Box sx={{ fontSize: '13px', fontWeight: 600, color: ui.ink, marginBottom: '6px' }}>
        {children}
        {required && <Box component="span" sx={{ color: LEVEL_COLORS.red.text }}> *</Box>}
        {hint && <Box component="span" sx={{ color: ui.muted, fontWeight: 400 }}> · {hint}</Box>}
    </Box>
)

export const ErrorText: React.FC<{ text?: string }> = ({ text }) =>
    text ? <Box sx={{ fontSize: '12px', color: LEVEL_COLORS.red.text, marginTop: '4px' }}>{text}</Box> : null

// Цепочка этапов «Модератор ✓ → Пән басшысы ✓ → Ментор 3/5»
export const Stages: React.FC<{ items: { text: string; state: 'on' | 'cur' | 'off' }[] }> = ({ items }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
        {items.map((s, i) => (
            <React.Fragment key={i}>
                {i > 0 && <Box component="span" sx={{ color: ui.muted, fontSize: '12px' }}>→</Box>}
                <Pill tone={s.state === 'on' ? 'ok' : s.state === 'cur' ? 'wait' : 'grey'}>{s.text}</Pill>
            </React.Fragment>
        ))}
    </Box>
)

type Btn = ButtonProps & { component?: React.ElementType }

// color="error" - приглушённый красный тон вместо яркой заливки темы
const badFilled = {
    background: tone.bad.bg,
    color: tone.bad.text,
    border: `1px solid ${tone.bad.border}`,
    '&:hover': { background: tone.bad.border },
}
const badOutlined = { color: tone.bad.text, borderColor: tone.bad.border, '&:hover': { borderColor: tone.bad.text, background: tone.bad.bg } }

export const PrimaryButton: React.FC<Btn> = (props) => (
    <Button
        variant="contained"
        disableElevation
        {...props}
        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, ...(props.color === 'error' ? badFilled : {}), ...(props.sx as any) }}
    />
)

export const OutlineButton: React.FC<Btn> = (props) => (
    <Button
        variant="outlined"
        {...props}
        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, ...(props.color === 'error' ? badOutlined : {}), ...(props.sx as any) }}
    />
)

// Модалка CRM: заголовок, подзаголовок, содержимое; на телефоне почти во всю ширину
export const CrmDialog: React.FC<{ title: string; sub?: React.ReactNode; onClose: () => void; children: React.ReactNode }> = ({ title, sub, onClose, children }) => (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', margin: '12px', width: 'calc(100% - 24px)' } }}>
        <Box sx={{ position: 'relative', padding: '20px' }}>
            <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 10, right: 10 }} aria-label="close">
                <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ fontWeight: 800, fontSize: '17px', color: ui.ink, paddingRight: '28px' }}>{title}</Box>
            {sub && <Box sx={{ fontSize: '13px', color: ui.muted, marginTop: '2px' }}>{sub}</Box>}
            {children}
        </Box>
    </Dialog>
)
